import { useState, useEffect } from 'react';
import { Check, Users, Trophy } from 'lucide-react';
import {
  PredictionGame,
  PredictionSelection,
  optInPlayer,
  submitRoundPicks,
  subscribeToMySelection,
} from '../../services/prediction';
import type { SongRequest } from '../../services/requests';
import { FirebaseError } from 'firebase/app';
import toast from 'react-hot-toast';

function seededShuffle<T>(items: T[], seed: string): T[] {
  const shuffled = [...items];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  }
  for (let i = shuffled.length - 1; i > 0; i--) {
    hash = ((hash << 5) - hash + i) | 0;
    const j = ((hash < 0 ? -hash : hash) % (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

interface PredictionPlayModalProps {
  game: PredictionGame;
  eventId: string;
  attendeeId: string;
  nickname?: string;
  queuedSongs: SongRequest[];
  onClose: () => void;
}

export default function PredictionPlayModal({
  game,
  eventId,
  attendeeId,
  nickname,
  queuedSongs,
  onClose,
}: PredictionPlayModalProps) {
  const [selection, setSelection] = useState<PredictionSelection | null>(null);
  const [selectedPicks, setSelectedPicks] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [joining, setJoining] = useState(false);
  const [currentRound, setCurrentRound] = useState<1 | 2 | 3 | 4>(1);
  const [eliminated, setEliminated] = useState(false);
  const [eliminatedRound, setEliminatedRound] = useState<number | null>(null);

  useEffect(() => {
    const unsub = subscribeToMySelection(game.id, attendeeId, (sel) => {
      setSelection(sel);
      if (!sel) return;

      // Determine current round from selection state
      if (sel.round4Picks) {
        setCurrentRound(4);
      } else if (sel.round3Eliminated === true) {
        setEliminated(true);
        setEliminatedRound(3);
      } else if (sel.round3Eliminated === false) {
        setCurrentRound(4);
      } else if (sel.round2Eliminated === true) {
        setEliminated(true);
        setEliminatedRound(2);
      } else if (sel.round2Eliminated === false) {
        setCurrentRound(3);
      } else if (sel.round1Eliminated === true) {
        setEliminated(true);
        setEliminatedRound(1);
      } else if (sel.round1Eliminated === false) {
        setCurrentRound(2);
      }
    });
    return () => unsub();
  }, [game.id, attendeeId]);

  const handleOptIn = async () => {
    if (joining) return;
    setJoining(true);
    try {
      await optInPlayer(game.id, eventId, attendeeId, nickname);
      toast.success("You're in!");
    } catch (error) {
      console.error('Prediction opt-in failed:', error);
      toast.error('Failed to join game');
    } finally {
      setJoining(false);
    }
  };

  const togglePick = (requestId: string) => {
    setSelectedPicks((prev) => {
      const next = new Set(prev);
      if (next.has(requestId)) {
        next.delete(requestId);
      } else {
        next.add(requestId);
      }
      return next;
    });
  };

  const handleSubmitPicks = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const picks = Array.from(selectedPicks);
      const result = await submitRoundPicks(game.id, currentRound, picks);

      if (result.eliminated) {
        setEliminated(true);
        setEliminatedRound(currentRound);
        toast.error(`Eliminated in Round ${currentRound}!`);
      } else if (currentRound === 4) {
        toast.success('Answer locked in! Wait for the song to play...');
      } else {
        toast.success(`Moving to Round ${currentRound + 1}!`);
      }
      setSelectedPicks(new Set());
    } catch (error) {
      const isRefusal = error instanceof FirebaseError
        && (error.code === 'functions/failed-precondition' || error.code === 'functions/invalid-argument');
      if (isRefusal || !(error instanceof FirebaseError)) {
        toast.error((error as Error).message || 'Failed to submit picks');
      } else {
        toast.error('Failed to submit picks');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const roundConfig: Record<number, { count: number; label: string; subtitle: string }> = {
    1: { count: 5, label: 'Round 1: Pick 5', subtitle: 'Pick 5 songs you think could be next' },
    2: { count: 3, label: 'Round 2: Narrow to 3', subtitle: 'Narrow your picks to 3' },
    3: { count: 2, label: 'Round 3: Narrow to 2', subtitle: 'Getting close! Pick your top 2' },
    4: { count: 1, label: 'Final Answer', subtitle: 'Which one is next? Lock in your answer!' },
  };

  const getAvailableSongs = (): SongRequest[] => {
    const seed = `${attendeeId}-${game.id}-${currentRound}`;
    if (currentRound === 2 && selection?.round1Picks) {
      const r1Set = new Set(selection.round1Picks);
      return seededShuffle(queuedSongs.filter((s) => r1Set.has(s.id)), seed);
    }
    if (currentRound === 3 && selection?.round2Picks) {
      const r2Set = new Set(selection.round2Picks);
      return seededShuffle(queuedSongs.filter((s) => r2Set.has(s.id)), seed);
    }
    if (currentRound === 4 && selection?.round3Picks) {
      const r3Set = new Set(selection.round3Picks);
      return seededShuffle(queuedSongs.filter((s) => r3Set.has(s.id)), seed);
    }
    return seededShuffle(queuedSongs, seed);
  };

  const hasCompletedCurrentRound = (): boolean => {
    if (!selection) return false;
    if (currentRound === 1) return selection.round1Picks !== undefined;
    if (currentRound === 2) return selection.round2Picks !== undefined;
    if (currentRound === 3) return selection.round3Picks !== undefined;
    if (currentRound === 4) return selection.round4Picks !== undefined;
    return false;
  };

  // Reveal state
  if (game.status === 'revealing') {
    const isWinner = game.winnerIds.includes(attendeeId);
    const pickedCorrectly = selection?.round4Correct === true;

    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4">
          {isWinner ? (
            <>
              <div className="text-center space-y-2">
                <div className="text-4xl">🎉</div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">You Won!</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">You picked the right song! Check your prizes.</p>
              </div>
              <div className="px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-center">
                <Trophy className="w-6 h-6 text-green-600 mx-auto mb-1" />
                <p className="text-sm font-medium text-green-700 dark:text-green-300">Prize awarded!</p>
              </div>
            </>
          ) : pickedCorrectly ? (
            <div className="text-center space-y-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">You got it right!</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                But {game.winnerNicknames[0] || 'someone'} was faster. Great prediction though!
              </p>
            </div>
          ) : selection?.round4Picks ? (
            <div className="text-center space-y-2">
              <div className="text-3xl">😔</div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Wrong pick!</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {game.winnerNicknames.length > 0
                  ? `Winner: ${game.winnerNicknames.join(', ')}`
                  : 'No one picked correctly!'}
              </p>
            </div>
          ) : (
            <div className="text-center space-y-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Game Over</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {game.winnerNicknames.length > 0
                  ? `Winner: ${game.winnerNicknames.join(', ')}`
                  : 'No one picked correctly!'}
              </p>
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Eliminated state (rounds 1-3)
  if (eliminated) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4">
          <div className="text-center space-y-2">
            <div className="text-3xl">😔</div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Eliminated in Round {eliminatedRound}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">The answer song wasn't in your picks. Better luck next time!</p>
          </div>

          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">Players remaining</p>
            <p className="text-lg font-bold text-gray-900 dark:text-white">
              {eliminatedRound === 1 ? game.round1Survivors : eliminatedRound === 2 ? game.round2Survivors : game.round3Survivors}
            </p>
          </div>

          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Waiting for reveal (completed round 4)
  if (selection?.round4Picks && hasCompletedCurrentRound()) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4">
          <div className="text-center space-y-2">
            <div className="text-3xl animate-pulse">✨</div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Answer locked in!</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Waiting for the song to play to see if you're right...</p>
          </div>

          <div className="px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-center">
            <Users className="w-5 h-5 text-green-600 mx-auto mb-1" />
            <p className="text-sm text-green-700 dark:text-green-300">{game.round4Survivors} finalist{game.round4Survivors !== 1 ? 's' : ''} waiting</p>
          </div>
        </div>
      </div>
    );
  }

  // Join prompt (not opted in yet)
  if (!selection) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4">
          <div className="text-center space-y-2">
            <div className="text-3xl">🎯</div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Prediction Game!</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Guess which song plays next. Narrow your picks across 4 rounds to win!</p>
          </div>

          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Users className="w-4 h-4" />
            <span>{game.playerCount} player{game.playerCount !== 1 ? 's' : ''} playing</span>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleOptIn}
              disabled={joining}
              className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 font-medium disabled:opacity-50"
            >
              {joining ? 'Joining...' : 'Play'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Round selection UI
  const config = roundConfig[currentRound];
  const availableSongs = getAvailableSongs();
  const alreadySubmitted = hasCompletedCurrentRound();

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white dark:bg-gray-900 rounded-t-xl sm:rounded-xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Progress bar — 4 rounds */}
        <div className="flex gap-1 p-3 pb-0">
          {[1, 2, 3, 4].map((r) => (
            <div
              key={r}
              className={`h-1.5 flex-1 rounded-full ${
                r < currentRound
                  ? 'bg-green-500'
                  : r === currentRound
                    ? 'bg-primary'
                    : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>

        {/* Header */}
        <div className="p-4 pb-2">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">{config.label}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">{config.subtitle}</p>
          <p className="text-xs text-primary font-medium mt-1">
            {selectedPicks.size}/{config.count} selected
          </p>
        </div>

        {/* Song list */}
        <div className="flex-1 overflow-y-auto px-4 pb-2 space-y-1">
          {availableSongs.map((song) => {
            const isSelected = selectedPicks.has(song.id);
            return (
              <button
                key={song.id}
                onClick={() => {
                  if (alreadySubmitted) return;
                  if (!isSelected && selectedPicks.size >= config.count) return;
                  togglePick(song.id);
                }}
                disabled={alreadySubmitted}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                  isSelected
                    ? 'bg-primary/10 border border-primary'
                    : 'bg-gray-50 dark:bg-gray-800/50 border border-transparent hover:bg-gray-100 dark:hover:bg-gray-800'
                } ${alreadySubmitted ? 'opacity-50' : ''}`}
              >
                {song.song.albumArtUrl && (
                  <img src={song.song.albumArtUrl} alt="" className="w-10 h-10 rounded flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 dark:text-white text-sm truncate">{song.song.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{song.song.artist}</p>
                </div>
                {isSelected && (
                  <Check className="w-5 h-5 text-primary flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>

        {/* Submit */}
        {!alreadySubmitted && (
          <div className="p-4 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSubmitPicks}
              disabled={selectedPicks.size !== config.count || submitting}
              className="w-full px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 font-medium disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : currentRound === 4 ? 'Lock In Final Answer' : `Submit ${config.count} Picks`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
